import type React from "react";
import { useState } from "react";
import { Layout } from "../layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import { Loader } from "../layout/Loader";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Copy, ClipboardCopy } from "lucide-react";

interface OrderStatusResult {
	invoice_number: string;
	status: string;
	success: boolean;
}

export const OrderStatusLookup: React.FC = () => {
	const [inputValue, setInputValue] = useState<string>("");
	const [results, setResults] = useState<OrderStatusResult[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [outputText, setOutputText] = useState<string>("");
	const { toast } = useToast();

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setInputValue(e.target.value);
	};

	const parseInvoiceNumbers = (input: string): string[] => {
		// Split by comma or newline
		const lines = input.split(/[,\n]+/);

		// Trim each line and filter out empty values
		return lines.map((line) => line.trim()).filter((line) => line.length > 0);
	};

	const handleLookup = async () => {
		try {
			setLoading(true);
			const invoiceNumbers = parseInvoiceNumbers(inputValue);

			if (invoiceNumbers.length === 0) {
				toast({
					title: "Error",
					description: "Please enter at least one invoice number",
					variant: "destructive",
				});
				return;
			}

			const response = await api.getBulkOrderStatus(invoiceNumbers);

			if (response && response.success) {
				setResults(response.data);

				// Format the output text
				const formattedOutput = response.data
					.map(
						(result: OrderStatusResult) =>
							`${result.invoice_number} - ${result.status}`,
					)
					.join("\n\n");

				setOutputText(formattedOutput);
			} else {
				toast({
					title: "Error",
					description: response?.error || "Failed to fetch order statuses",
					variant: "destructive",
				});
			}
		} catch (error) {
			console.error("Error looking up order statuses:", error);
			toast({
				title: "Error",
				description: "An unexpected error occurred",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const copyToClipboard = () => {
		navigator.clipboard.writeText(outputText);
		toast({
			title: "Success",
			description: "Results copied to clipboard",
			variant: "default",
		});
	};

	return (
		<>
			<Card>
				<CardHeader>
					<CardTitle>Enter Invoice Numbers</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="text-sm text-gray-500 mb-4">
						Enter invoice numbers separated by commas or new lines. Empty values
						will be ignored.
					</p>
					<Textarea
						placeholder="e.g. 63218853, 63229014, 63233225"
						className="min-h-[200px] mb-4"
						value={inputValue}
						onChange={handleInputChange}
					/>
					<Button onClick={handleLookup} disabled={loading} className="w-full">
						{loading ? <Loader /> : "Look Up Order Status"}
					</Button>
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle>Results</CardTitle>
					{results.length > 0 && (
						<Button
							variant="outline"
							size="sm"
							onClick={copyToClipboard}
							className="flex items-center gap-1"
						>
							<ClipboardCopy size={16} />
							<span>Copy</span>
						</Button>
					)}
				</CardHeader>
				<CardContent>
					{results.length > 0 ? (
						<div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md min-h-[200px] whitespace-pre-line">
							{outputText}
						</div>
					) : (
						<div className="flex items-center justify-center min-h-[200px] text-gray-500">
							{loading ? (
								<Loader />
							) : (
								<p>
									No results to display. Enter invoice numbers and click "Look
									Up Order Status".
								</p>
							)}
						</div>
					)}
				</CardContent>
			</Card>
		</>
	);
};

export default OrderStatusLookup;
